/**
 * Dispatch Stock Transfer – Plant: the rules the page and the API share.
 *
 * The form config is `dispatchStockTransfer` in `documents/dispatch.ts`; this file holds what that
 * config cannot say — the carriage rule this screen uses, the Preview blockers and the address line.
 *
 *     draft ──preview──▶ previewed ──final submit──▶ submitted
 *       ▲                   │                            │
 *       └───────edit────────┘                            └──cancel──▶ cancelled (stock returns)
 */
import type { TransportMode } from "./documents/types";

export const DISPATCH_STOCK_TRANSFER_FORM = "dispatch_stock_transfer";

/**
 * The header fields the dedicated page renders, pinned against the config's `headerFields` by a
 * test so the two declarations cannot drift.
 */
export const DISPATCH_STOCK_TRANSFER_HEADER_FIELDS = ["dest_plant"] as const;

/** seedtrac's Payment Mode options, stored under the same values every arrival form uses. */
export const DISPATCH_PAYMENT_MODES = [
  { value: "TBB", label: "TBB" },
  { value: "to_pay", label: "To Pay" },
  { value: "paid", label: "Paid" },
] as const;
export type DispatchPaymentMode =
  (typeof DISPATCH_PAYMENT_MODES)[number]["value"];

export const paymentModeLabel = (v: string | null | undefined): string =>
  DISPATCH_PAYMENT_MODES.find((m) => m.value === v)?.label ?? "";

export interface DispatchTransport {
  mode?: TransportMode | null | undefined;
  transportName?: string | null | undefined;
  vehicleNumber?: string | null | undefined;
  lorryReceiptNumber?: string | null | undefined;
  paymentMode?: string | null | undefined;
  courierName?: string | null | undefined;
  docketNo?: string | null | undefined;
  deliveryPersonName?: string | null | undefined;
}

/**
 * The carriage fields still blank, in screen order.
 *
 * Lorry Receipt No. is NOT required here: seedtrac's screen puts no asterisk on it, while the
 * arrival forms' shared `TRANSPORT_FIELD_MODES` does. Courier and Hand Delivery ask for what the
 * arrival forms ask for under those modes.
 */
export function missingDispatchTransport(t: DispatchTransport): string[] {
  const blank = (v: string | null | undefined) => !(v ?? "").trim();
  const missing: string[] = [];
  if (!t.mode) return ["Mode of Transit"];
  if (t.mode === "transport") {
    if (blank(t.transportName)) missing.push("Transport Name");
    if (blank(t.vehicleNumber)) missing.push("Vehicle No.");
    if (blank(t.paymentMode)) missing.push("Payment Mode");
  } else if (t.mode === "courier") {
    if (blank(t.courierName)) missing.push("Courier Name");
    if (blank(t.docketNo)) missing.push("Docket No.");
  } else if (blank(t.deliveryPersonName)) {
    missing.push("Delivery Person Name");
  }
  return missing;
}

/** What stops Preview: the red-asterisk fields and at least one lot. */
export function dispatchStockTransferBlockers(doc: {
  destFacilityId: number | null | undefined;
  transport: DispatchTransport | null | undefined;
  lineCount: number;
}): string[] {
  const missing: string[] = [];
  if (!doc.destFacilityId) missing.push("Plant Name");
  missing.push(...missingDispatchTransport(doc.transport ?? {}));
  if (doc.lineCount === 0) missing.push("at least one lot");
  return missing;
}

/** The Address row: the plant master's address parts, comma-joined, blanks skipped. */
export function plantAddressLine(f: {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
}): string {
  return [f.address, f.city, f.state, f.pincode]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(", ");
}
